/**
 * PlaygroundBanner — floating watermark overlay shown when playground mode
 * is active. The text drifts around the viewport as a semi-transparent
 * watermark so users always know they are in demo mode, without blocking
 * any interactive content.
 */
import React, { useEffect, useRef } from 'react';
import { usePlayground } from './PlaygroundContext.jsx';
import './PlaygroundBanner.css';

const WATERMARK_TEXT = 'Playground Mode — All data is session-only';

const PlaygroundBanner = () => {
    const { isPlaygroundMode } = usePlayground();
    const containerRef = useRef(null);
    const posRef = useRef({ x: 60, y: 80 });
    const velRef = useRef({ dx: 0.4, dy: 0.3 });
    const rafRef = useRef(null);

    useEffect(() => {
        if (!isPlaygroundMode) return;

        const animate = () => {
            const el = containerRef.current;
            if (!el) return;

            const parent = el.parentElement;
            if (!parent) return;

            const pw = parent.clientWidth;
            const ph = parent.clientHeight;
            const ew = el.offsetWidth;
            const eh = el.offsetHeight;

            let { x, y } = posRef.current;
            let { dx, dy } = velRef.current;

            x += dx;
            y += dy;

            // Bounce off edges
            if (x + ew > pw || x < 0) {
                dx = -dx;
                x = Math.max(0, Math.min(x, pw - ew));
            }
            if (y + eh > ph || y < 0) {
                dy = -dy;
                y = Math.max(0, Math.min(y, ph - eh));
            }

            posRef.current = { x, y };
            velRef.current = { dx, dy };

            el.style.transform = `translate(${x}px, ${y}px)`;
            rafRef.current = requestAnimationFrame(animate);
        };

        rafRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [isPlaygroundMode]);

    if (!isPlaygroundMode) return null;

    return (
        <div className="playground-watermark" ref={containerRef}>
            {WATERMARK_TEXT}
        </div>
    );
};

export default PlaygroundBanner;
